import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Phone, CloudRain, Droplets, AlertTriangle, Clock, Calculator } from 'lucide-react';
import { useSEO, JsonLd } from '../utils/seo';
import { categories } from '../data/products';
import { getFallPhase } from '../utils/season';
/**
 * Fall driveway + drainage prep landing page.
 *
 * Residential construction demand is soft, so this page deliberately does not
 * sell improvement — it sells two things people buy in bad weather regardless
 * of the economy: fixing a driveway that has already failed, and preventing
 * one that is about to. Both are urgent, both are cheap relative to the
 * alternative, and both have a real deadline that is not a marketing device:
 * compacted rock needs dry ground to lock up, so the work genuinely has to
 * happen before the rain sets in.
 *
 * Every material, guide, and price here already exists elsewhere on the site.
 * This page's job is to assemble them around ONE season and ONE deadline, so
 * it can carry fall ad traffic and rank for the weather-prep searches that
 * spike after the first storm.
 */
/**
 * The season's four materials, each with the ONE line that matters in a fall
 * context. The catalog descriptions are thorough but long; here the job is to
 * tell someone which of the four is theirs in a glance.
 */
const SEASON_MATERIALS: {
  slug: string;
  role: string;
}[] = [{
  slug: '1-25-minus',
  role: 'The structural base. Goes underneath, carries the load, stops the ruts coming back.'
}, {
  slug: '5-8-minus',
  role: 'The driving surface. Compacts smooth over base rock, and fills potholes properly.'
}, {
  slug: '7-8-drain-rock',
  role: 'Washed and open. French drains, footing drains, and anywhere water needs a path.'
}, {
  slug: '5-8-clear',
  role: 'The tidy clean rock. Side yards, foundation strips, and narrow hand-shovelled runs.'
}];
type Symptom = {
  icon: typeof CloudRain;
  problem: string;
  cause: string;
  fix: string;
  material: string;
  materialSlug: string;
  guide: string;
  guideSlug: string;
};
const SYMPTOMS: Symptom[] = [{
  icon: AlertTriangle,
  problem: 'Ruts and soft spots that come back every year',
  cause: 'There is no structural base under the surface rock. Each winter the tires push the top layer down into saturated soil, and by spring the rock is gone — mixed into the mud rather than sitting on top of it.',
  fix: 'Build a real base: 4" of coarse 1.25" Minus compacted in two lifts, capped with 2–3" of 5/8" Minus. On genuinely soft ground, geotextile fabric underneath keeps the two from ever mixing.',
  material: '1.25" Minus',
  materialSlug: '1-25-minus',
  guide: 'Muddy Driveway Fix',
  guideSlug: 'muddy-driveway-fix'
}, {
  icon: Droplets,
  problem: 'Water stands in the same spot after every storm',
  cause: 'Water has nowhere to go. Either the grade sends it to a low point with no outlet, or the soil there is too tight to absorb it. Adding more surface rock on top does nothing — it just gives the water something new to sit in.',
  fix: 'Give the water a path. A trench of washed 7/8" Drain Rock around perforated pipe moves it away from the low point instead of holding it there.',
  material: '7/8" Drain Rock',
  materialSlug: '7-8-drain-rock',
  guide: 'French Drain',
  guideSlug: 'french-drain'
}, {
  icon: CloudRain,
  problem: 'Potholes that fill with water and grow',
  cause: 'A pothole is a bowl. Once it holds water the base under it softens, the edges break down, and the hole widens with every vehicle. Left through a wet winter, a pothole you could have filled in October becomes a base repair in March.',
  fix: 'Fill to 3" below grade with 1.25" Minus, compact it, then cap with 5/8" Minus and compact again. Never fill a pothole with clean rock — without fines it will not lock up and it works straight back out.',
  material: '5/8" Minus',
  materialSlug: '5-8-minus',
  guide: 'Driveway Top-Off',
  guideSlug: 'driveway-top-off'
}, {
  icon: Droplets,
  problem: 'A wet, mossy side yard against the house',
  cause: 'Narrow, shaded runs beside a house stay wet from October through May. Bare soil there holds water against the foundation and turns to mud underfoot the moment anyone walks it.',
  fix: '3–4" of washed 5/8" Crushed Clear over fabric. It drains straight through, stays put, and never becomes mud — unlike a minus rock, which would compact and trap the water it is supposed to shed.',
  material: '5/8" Crushed Clear',
  materialSlug: '5-8-clear',
  guide: 'French Drain',
  guideSlug: 'french-drain'
}];
const PHASE_COPY: Record<'early' | 'peak' | 'late' | 'off', {
  eyebrow: string;
  deadline: string;
}> = {
  early: {
    eyebrow: 'Fall prep season',
    deadline: 'The ground is still dry. This is the easiest, cheapest window all year to fix a driveway — rock compacts properly now and holds through the winter.'
  },
  peak: {
    eyebrow: 'Beat the rain',
    deadline: 'This is the last reliable dry stretch. Once the ground saturates, gravel stops compacting and starts sinking — the same repair costs more rock and holds less well.'
  },
  late: {
    eyebrow: 'Rain has started',
    deadline: "The rain is here, so the job is repair rather than prep. It is still worth doing — a rutted driveway only gets worse from here — but expect to need a deeper base than you would have in September."
  },
  off: {
    eyebrow: 'Driveway & drainage',
    deadline: 'Gravel compacts best on dry ground. If your driveway rutted or flooded last winter, fixing it during a dry stretch costs less rock and holds far better than patching it in the wet.'
  }
};
export function FallPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: 'Fall Driveway & Drainage Prep — Fix It Before the Rain | 206Gravel',
    description: 'Rutted driveway, standing water, or potholes? Gravel only compacts on dry ground, so fall is the window to fix it. Materials, depths, and delivered pricing across the Puget Sound.',
    path: '/fall-prep'
  });
  const phase = getFallPhase() ?? 'off';
  const copy = PHASE_COPY[phase];
  const productMap = new Map(categories.flatMap(c => c.products).map(p => [p.slug, p]));
  const products = SEASON_MATERIALS.map(m => {
    const product = productMap.get(m.slug);
    return product ? {
      ...product,
      role: m.role
    } : null;
  }).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const faqs = [{
    q: 'When is it too late to fix a gravel driveway?',
    a: 'You can place gravel year-round, but you cannot compact it well on saturated ground. Once the soil is holding water, the rock presses into the mud instead of locking together, so you need more of it and it does not hold as long. A dry stretch — any time from late summer through early fall — is worth waiting for if you have the choice.'
  }, {
    q: 'Why does my driveway get muddy in the same spot every winter?',
    a: 'Almost always because there is no structural base under the surface rock, not because the surface rock is wrong. Push a length of rebar into the driveway by hand: if it slides in past four or five inches, you are standing on soil, not base. The fix is 4" of compacted 1.25" Minus underneath, capped with 5/8" Minus.'
  }, {
    q: 'Should I use minus or clean rock for drainage?',
    a: 'Clean. Minus rock contains fines that compact into a hard, largely sealed surface — good for driving on, wrong for moving water. Washed rock (7/8" Drain Rock, or 5/8" Crushed Clear for narrower runs) keeps its voids open so water passes through. Using minus in a drain trench is the single most common ordering mistake we see.'
  }, {
    q: 'How much gravel do I need to top off my driveway?',
    a: 'One yard of 5/8" Minus covers roughly 100 sq ft at 3" deep. For a typical 40 × 12 ft driveway that is about five yards for a surface top-off. Run your own dimensions through the calculator and it will round to a deliverable load and add about 15% for compaction.'
  }, {
    q: 'Can you still deliver in the rain?',
    a: 'Yes — we run through the winter. What changes is the drop site, not the delivery: a soft lawn or an unsupported shoulder can rut under a loaded truck, so we may ask to dump on the driveway or street instead and let you move it from there.'
  }];
  return <main className="min-h-screen bg-[#F2EFE8] dark:bg-[#262626]" data-id="element-3393" data-mp="bkup1b45yp4">
      <JsonLd data={[{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map(f => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.a
        }
      }))
    }, {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://206gravel.com/'
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Fall Driveway & Drainage Prep',
        item: 'https://206gravel.com/fall-prep'
      }]
    }]} data-id="element-3394" data-mp="38q2ygqu0us2x" />

      {/* Hero — the deadline IS the pitch, so it leads. */}
      <section className="relative overflow-hidden" data-id="element-3395" data-mp="2fx7sdce1u0zd">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/sWr6t1pMKENTQF4965m8Y3/muddy_driveway_hero.jpg)'
      }} aria-hidden="true" data-id="element-3396" data-mp="21wwzy4x3cec3" />
        <div className="absolute inset-0 bg-black/40" aria-hidden="true" data-id="element-3397" data-mp="2ul2m8w3smbe1" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 md:pb-28" data-id="element-3398" data-mp="2ymwi8r31mr7v">
          <nav className="flex items-center text-sm text-white/60 mb-10" aria-label="Breadcrumb" data-id="element-3399" data-mp="2kvj27n4cgbhl">
            <Link to="/" className="hover:text-white transition-colors" data-id="element-3400" data-mp="2pum819s5elqm">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 mx-2 shrink-0" data-id="element-3401" data-mp="3dkghdhd9ds64" />
            <span className="text-white font-medium" data-id="element-3402" data-mp="3di3sek932hjk">Fall prep</span>
          </nav>

          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-3xl" data-id="element-3403" data-mp="3icgi3c15kp3r">
            <span className="inline-flex items-center gap-2 bg-[#059F5F] px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white" data-id="element-3404" data-mp="1gj3nksuhq282">
              <Clock className="w-3.5 h-3.5" data-id="element-3405" data-mp="1kilziz4c32yr" />
              {copy.eyebrow}
            </span>
            <h1 className="mt-6 font-heading text-4xl md:text-6xl font-black leading-[1.05] tracking-tight text-white" data-id="element-3406" data-mp="16rfvqqu0uj62">
              Fix the driveway
              <br data-id="element-3407" data-mp="wv4bneo6wp12" />
              <span className="text-[#22c95c]" data-id="element-3408" data-mp="wvvzg3bjpigs">before the rain.</span>
            </h1>
            <p className="mt-6 max-w-2xl font-sub text-lg md:text-xl leading-relaxed text-white/85" data-id="element-3409" data-mp="11tebi2kv0dt0">
              {copy.deadline}
            </p>
            <div className="mt-9 flex flex-wrap gap-3" data-id="element-3410" data-mp="nuy3m8b9gh9a">
              <Link to="/#estimator" className="inline-flex items-center gap-2 bg-[#04A05C] px-7 py-4 font-bold text-white transition-colors hover:bg-[#17A347]" data-id="element-3411" data-mp="stiu3g0pnmkj">
                Price it delivered
                <ArrowRight className="w-4 h-4" data-id="element-3412" data-mp="49626uwbsdz5" />
              </Link>
              <a href="tel:206-650-0129" className="inline-flex items-center gap-2 border border-white/30 px-7 py-4 font-bold text-white transition-colors hover:bg-white/10" data-id="element-3413" data-mp="i85ovx0a2iso">
                <Phone className="w-4 h-4 text-[#22c95c]" data-id="element-3414" data-mp="e8n5j5o3ufry" />
                (206) 650-0129
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Symptom → cause → fix. Diagnosis, not a product grid: people arrive
          describing a problem, not naming a material. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-3415" data-mp="11xxwjvfnkvsw">
        <div className="mb-3 h-1 w-16 bg-brand-orange" data-id="element-3416" data-mp="wxtp0uwq3bet" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-3417" data-mp="1aw1md3xo8eqi">
          What's actually wrong, and what fixes it
        </h2>
        <p className="mt-3 max-w-2xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3418" data-mp="3ku0fet4ey9k">
          Four failures account for nearly every fall call we take. Find yours —
          the cause matters more than the symptom, because the wrong material
          makes three of these worse.
        </p>

        <div className="mt-10 border-t border-stone-300 dark:border-white/15" data-id="element-3419" data-mp="hc8o3yxz3nmy">
          {SYMPTOMS.map((s, idx) => {
          const Icon = s.icon;
          return <div key={s.problem} className="grid gap-6 border-b border-stone-300 py-8 md:grid-cols-12 md:gap-8 dark:border-white/15" data-id="element-3420" data-mp="mcw9o53fnc9s" data-mpi={idx}>
                <div className="md:col-span-5" data-id="element-3421" data-mp="w8g5yslwscz2">
                  <div className="flex items-start gap-3" data-id="element-3422" data-mp="w9r7smvsnanj">
                    <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center bg-brand-orange/10 text-brand-orange" data-id="element-3423" data-mp="10aunn9dud6or">
                      <Icon className="h-5 w-5" data-id="element-3424" data-mp="2y1xd7v64nbta" />
                    </span>
                    <h3 className="font-heading text-xl font-bold leading-snug text-brand-charcoal dark:text-white" data-id="element-3425" data-mp="322z9q48nt9rk">
                      {s.problem}
                    </h3>
                  </div>
                </div>
                <div className="md:col-span-7" data-id="element-3426" data-mp="2pq5f7jjnsbkq">
                  <p className="text-[13px] font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-3427" data-mp="2ksc5whn271g8">
                    Why it happens
                  </p>
                  <p className="mt-1.5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3428" data-mp="2gp76iny9zfvt">
                    {s.cause}
                  </p>
                  <p className="mt-4 text-[13px] font-bold uppercase tracking-wider text-[#04A05C]" data-id="element-3429" data-mp="38hy7fbqwlsre">
                    The fix
                  </p>
                  <p className="mt-1.5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3430" data-mp="34ipc42g7tmtq">
                    {s.fix}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-3431" data-mp="1t1pmjel95m3e">
                    <Link to={`/shop/${s.materialSlug}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange transition-colors hover:text-brand-orange/80" data-id="element-3432" data-mp="1o2mermdw11rq">
                      Order {s.material}
                      <ArrowRight className="w-4 h-4" data-id="element-3433" data-mp="3gqssipwbvc81" />
                    </Link>
                    <Link to={`/projects/${s.guideSlug}`} className="text-sm font-semibold text-stone-600 underline underline-offset-2 transition-colors hover:text-brand-charcoal dark:text-stone-300 dark:hover:text-white" data-id="element-3434" data-mp="32yw3iupyhifv">
                      Full {s.guide} guide
                    </Link>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* The one distinction that decides whether the job works at all. */}
      <section className="bg-[#e8ede4] dark:bg-white/[0.04] border-y border-stone-300 dark:border-white/10" data-id="element-3435" data-mp="3lr6ubq7c5pxm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16" data-id="element-3436" data-mp="2a9yn6x5b1vpr">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-3437" data-mp="2ac1o4c7zx999">
            Minus compacts. Clean drains. Do not swap them.
          </h2>
          <p className="mt-3 max-w-3xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3438" data-mp="26tm3fekrate5">
            This is the difference behind most wasted fall orders. Both are
            crushed rock; the only difference is whether the fines were washed
            out, and that one detail reverses what the material does.
          </p>
          <div className="mt-8 grid gap-5 md:grid-cols-2" data-id="element-3439" data-mp="1o0qira0jj1pg">
            <div className="border-l-4 border-[#04A05C] bg-white p-6 dark:bg-[#1f1f1f]" data-id="element-3440" data-mp="1nz6y777uq4hv">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#04A05C]" data-id="element-3441" data-mp="1j3zzyexcps0c">
                Minus — fines left in
              </p>
              <p className="mt-2 font-heading text-xl font-bold text-brand-charcoal dark:text-white" data-id="element-3442" data-mp="1t033np7upype">
                Locks up hard. Drive on it.
              </p>
              <p className="mt-2.5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3443" data-mp="158fkjtgtzrxo">
                Driveways, parking pads, base under concrete, pothole repair.
                Sheds water off the surface rather than through it — which is
                exactly why it is wrong in a drain trench.
              </p>
              <p className="mt-3 text-sm font-semibold text-stone-500 dark:text-stone-400" data-id="element-3444" data-mp="3a401di8kwb8j">
                1.25" Minus (base) · 5/8" Minus (surface)
              </p>
            </div>
            <div className="border-l-4 border-[#2563eb] bg-white p-6 dark:bg-[#1f1f1f]" data-id="element-3445" data-mp="2hf2nrsfbb2m4">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#2563eb]" data-id="element-3446" data-mp="2hhff2w6ympbu">
                Clean — washed
              </p>
              <p className="mt-2 font-heading text-xl font-bold text-brand-charcoal dark:text-white" data-id="element-3447" data-mp="2qepp6e81k8f6">
                Stays open. Water moves through.
              </p>
              <p className="mt-2.5 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3448" data-mp="5ps89ioeqwfx">
                French drains, footing drains, side yards, dry wells. It will
                never compact, so it cannot carry traffic — put it where water
                needs a path, not where tires go.
              </p>
              <p className="mt-3 text-sm font-semibold text-stone-500 dark:text-stone-400" data-id="element-3449" data-mp="fsymjcvtsn8c">
                7/8" Drain Rock (volume) · 5/8" Crushed Clear (tight runs)
              </p>
            </div>
          </div>
          <Link to="/compare" className="mt-7 inline-flex items-center gap-1.5 text-sm font-bold text-brand-orange transition-colors hover:text-brand-orange/80" data-id="element-3450" data-mp="703pv7kfbzu6">
            Compare every material side by side
            <ArrowRight className="w-4 h-4" data-id="element-3451" data-mp="3t7lihpp07zs7" />
          </Link>
        </div>
      </section>

      {/* The four materials this season actually needs, with live pricing. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-3452" data-mp="3k86paihgdb40">
        <div className="mb-3 h-1 w-16 bg-brand-orange" data-id="element-3453" data-mp="1mhnc6xw1t264" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-3454" data-mp="1mhdnreqnj32j">
          The four materials fall runs on
        </h2>
        <p className="mt-3 max-w-2xl text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3455" data-mp="21q4rnysm7ev5">
          Delivered across the Puget Sound, priced per yard with the haul added
          once at checkout by your address.
        </p>
        <ul className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-3456" data-mp="1rz0bwwv47pz9">
          {products.map((p, idx) => <motion.li key={p.slug} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.24,
          delay: Math.min(idx, 3) * 0.04
        }} data-id="element-3457" data-mp="1ww8wt57ne847" data-mpi={idx}>
              <Link to={`/shop/${p.slug}`} className="group flex h-full flex-col border border-stone-200 bg-white transition-shadow hover:shadow-md dark:border-white/10 dark:bg-white/[0.04]" data-id="element-3458" data-mp="192ha6smn54cq">
                <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="h-40 w-full object-cover" data-id="element-3459" data-mp="33cchpdafqio2" />
                <div className="flex flex-1 flex-col p-5" data-id="element-3460" data-mp="3ihglgjguk0hv">
                  <h3 className="font-heading text-lg font-bold text-brand-charcoal transition-colors group-hover:text-brand-orange dark:text-white" data-id="element-3461" data-mp="3ihyqa4hkrn1e">
                    {p.name}
                  </h3>
                  <p className="mt-1.5 flex-1 text-sm leading-relaxed text-stone-600 dark:text-stone-300" data-id="element-3462" data-mp="3rbbq7qe23owz">
                    {p.role}
                  </p>
                  <p className="mt-4 font-heading text-lg font-bold tabular-nums text-brand-charcoal dark:text-white" data-id="element-3463" data-mp="5212mq886t6s">
                    ${p.pricePerYard}
                    <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3464" data-mp="5k4r4b850gi">
                      /yd
                    </span>
                  </p>
                </div>
              </Link>
            </motion.li>)}
        </ul>
      </section>

      {/* Calculator hand-off — the next question after "which material". */}
      <section className="bg-[#575656]" data-id="element-3465" data-mp="2btidibdh6det">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-16" data-id="element-3466" data-mp="2bul4ejassfuq">
          <div className="grid gap-8 md:grid-cols-12 md:items-center" data-id="element-3467" data-mp="1j609qt8z3yrp">
            <div className="md:col-span-8" data-id="element-3468" data-mp="1e88je3nz8kpm">
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-white" data-id="element-3469" data-mp="1e7fhbfzyqjdt">
                Work out how much before you order
              </h2>
              <p className="mt-3 max-w-2xl text-white/80 leading-relaxed" data-id="element-3470" data-mp="1nytbd895aqzk">
                Enter your driveway or trench dimensions and the calculator
                returns the yardage, rounds it to a load we can actually haul,
                and drops it straight into the cart. Add about 15% for
                compaction on any minus material — the calculator handles it.
              </p>
            </div>
            <div className="md:col-span-4 flex flex-wrap gap-3 md:justify-end" data-id="element-3471" data-mp="156he2zxcmfwa">
              <Link to="/calculator" className="inline-flex items-center gap-2 bg-brand-orange px-6 py-3.5 font-bold text-white transition-colors hover:bg-brand-orange/90" data-id="element-3472" data-mp="2kn9c80hpgmrz">
                <Calculator className="w-4 h-4" data-id="element-3473" data-mp="2o685vpcmagoz" />
                Open the calculator
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs — the fall questions, schema'd. */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20" data-id="element-3474" data-mp="2e8kcb2qqzf8e">
        <div className="mb-3 h-1 w-16 bg-brand-orange" data-id="element-3475" data-mp="2j6ls9esvmyfn" />
        <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-brand-charcoal dark:text-white" data-id="element-3476" data-mp="2j73y935ccr0r">
          Fall questions we get every year
        </h2>
        <dl className="mt-10 divide-y divide-stone-300 dark:divide-white/15 border-t border-stone-300 dark:border-white/15" data-id="element-3477" data-mp="3bu7zcx1bcl79">
          {faqs.map(f => <div key={f.q} className="py-6" data-id="element-3478" data-mp="2x4sh65fulo36">
              <dt className="font-heading text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-3479" data-mp="324ofbo1v6lat">
                {f.q}
              </dt>
              <dd className="mt-2 text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3480" data-mp="3pt7m1z59mq0s">
                {f.a}
              </dd>
            </div>)}
        </dl>
      </section>

      {/* Closing CTA */}
      <section className="bg-[#006239]" data-id="element-3481" data-mp="8airxjvproor">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center" data-id="element-3482" data-mp="3d0fvkmegtcj">
          <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight text-white" data-id="element-3483" data-mp="3vh2s2jhhgsxu">
            Order by noon, dumped today
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-white/85 leading-relaxed" data-id="element-3484" data-mp="3qhyj9tyh0x81">
            Local trucks, delivered pricing before you commit, and no account to
            open. Most of the Puget Sound gets same-day service.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-id="element-3485" data-mp="3bt2dk3jvzwal">
            <Link to="/#estimator" className="inline-flex items-center gap-2 bg-white px-7 py-4 font-bold text-[#006239] transition-colors hover:bg-stone-100" data-id="element-3486" data-mp="3bqzbum9dgeq1">
              Get a delivered price
              <ArrowRight className="w-4 h-4" data-id="element-3487" data-mp="32ui13k4x6iut" />
            </Link>
            <a href="tel:206-650-0129" className="inline-flex items-center gap-2 border border-white/35 px-7 py-4 font-bold text-white transition-colors hover:bg-white/10" data-id="element-3488" data-mp="2xu2oiusgpfdw">
              <Phone className="w-4 h-4" data-id="element-3489" data-mp="1i9tn9jsnpim3" />
              (206) 650-0129
            </a>
          </div>
        </div>
      </section>
    </main>;
}